import type { ApiErrorCode } from '@comficonf/shared';
import { API_BASE_PATH, CSRF_HEADER } from '@comficonf/shared';
import type { z } from 'zod';

export type ApiErrorKind = ApiErrorCode | 'NETWORK_ERROR' | 'INVALID_RESPONSE' | (string & {});

/** Error raised for every failed API call, built from the `{ error: { code, message, details } }` body. */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly code: ApiErrorKind,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }

  /** True for apply failures after which the outcome on the server is unknown (disconnect or timeout). */
  get outcomeUnknown(): boolean {
    const outcome =
      typeof this.details === 'object' && this.details !== null
        ? (this.details as { outcome?: unknown }).outcome
        : undefined;
    return (
      outcome === 'unknown' || this.code === 'SERVER_DISCONNECTED' || this.code === 'BRIDGE_TIMEOUT'
    );
  }

  /** Bridge error code of a 502 BRIDGE_ERROR, when present. */
  get bridgeCode(): string | undefined {
    if (typeof this.details !== 'object' || this.details === null) return undefined;
    const code = (this.details as { bridgeCode?: unknown }).bridgeCode;
    return typeof code === 'string' ? code : undefined;
  }

  /** Field errors of a 400 VALIDATION_FAILED response. */
  get fieldErrors(): Array<{ path: string; message: string }> {
    if (!Array.isArray(this.details)) return [];
    return this.details.flatMap((entry: unknown) => {
      if (typeof entry !== 'object' || entry === null) return [];
      const { path, message } = entry as { path?: unknown; message?: unknown };
      if (typeof message !== 'string') return [];
      return [{ path: Array.isArray(path) ? path.join('.') : String(path ?? ''), message }];
    });
  }
}

export function isApiError(error: unknown, code?: ApiErrorKind): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

export interface ApiClientConfig {
  /** Origin of the backend when it is not the app's own origin, such as https://api.example.com. */
  baseUrl?: string;
  /** Supplies the session's CSRF token for POST, PATCH and DELETE requests. */
  getCsrfToken: () => string | undefined | Promise<string | undefined>;
  /** Called on 401 UNAUTHENTICATED, unless the request opted out. */
  onUnauthenticated?: (error: ApiError) => void;
  fetch?: typeof fetch;
}

export interface RequestOptions<T> {
  body?: unknown;
  /** Zod schema the success body must satisfy. Omit for 204 responses. */
  schema?: z.ZodType<T>;
  signal?: AbortSignal | undefined;
  query?: Record<string, string | number | undefined>;
  /** Skips the CSRF header (login and register). */
  skipCsrf?: boolean;
  /** Skips the redirect to the login page on 401. */
  skipAuthRedirect?: boolean;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface ApiClient {
  request<T = void>(method: HttpMethod, path: string, options?: RequestOptions<T>): Promise<T>;
  url(path: string, query?: RequestOptions<unknown>['query']): string;
}

function trimBase(baseUrl: string | undefined): string {
  return (baseUrl ?? '').replace(/\/+$/, '');
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function errorFromBody(status: number, body: unknown): ApiError {
  const error =
    typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
  if (typeof error === 'object' && error !== null) {
    const { code, message, details } = error as {
      code?: unknown;
      message?: unknown;
      details?: unknown;
    };
    if (typeof code === 'string') {
      return new ApiError(
        status,
        code,
        typeof message === 'string' && message !== '' ? message : defaultMessage(status),
        details,
      );
    }
  }
  return new ApiError(status, status >= 500 ? 'INTERNAL' : 'BAD_RESPONSE', defaultMessage(status));
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Please sign in to continue.';
  if (status === 403) return 'This action is not allowed.';
  if (status === 404) return 'Not found.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our side. Please try again.';
  return `The request failed (HTTP ${status}).`;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const base = trimBase(config.baseUrl);

  const url: ApiClient['url'] = (path, query) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined) params.set(key, String(value));
    }
    const search = params.toString();
    return `${base}${API_BASE_PATH}${path}${search ? `?${search}` : ''}`;
  };

  const request: ApiClient['request'] = async <T>(
    method: HttpMethod,
    path: string,
    options: RequestOptions<T> = {},
  ): Promise<T> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (MUTATING.has(method) && !options.skipCsrf) {
      const token = await config.getCsrfToken();
      if (token) headers[CSRF_HEADER] = token;
    }
    const doFetch = config.fetch ?? globalThis.fetch.bind(globalThis);
    let response: Response;
    try {
      response = await doFetch(url(path, options.query), {
        method,
        headers,
        credentials: 'include',
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal ?? null,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError(
        0,
        'NETWORK_ERROR',
        'Could not reach ComfiConf. Check your connection and try again.',
      );
    }

    const body = await readJson(response);
    if (!response.ok) {
      const error = errorFromBody(response.status, body);
      if (
        response.status === 401 &&
        error.code === 'UNAUTHENTICATED' &&
        !options.skipAuthRedirect
      ) {
        config.onUnauthenticated?.(error);
      }
      throw error;
    }
    if (!options.schema) return undefined as T;
    const parsed = options.schema.safeParse(body);
    if (!parsed.success) {
      throw new ApiError(
        response.status,
        'INVALID_RESPONSE',
        'The server sent a response this version of ComfiConf does not understand.',
        parsed.error.issues,
      );
    }
    return parsed.data;
  };

  return { request, url };
}
